import type { ComponentStatus, StatusComponent, StatusSnapshot, UptimeBand } from '@/types';
import { statusHeadlines, statusLabels, statusMessages, uptimeBandLabels } from '@/i18n';

const MINUTES_PER_DAY = 1440;

/**
 * Band thresholds for a day cell.
 *
 * Deliberately finer-grained than `ComponentStatus`: most real degradations sit
 * between 95% and 99.9%, which a binary up/down bar would paint solid green.
 */
export function uptimeBand(uptime: number | null | undefined): UptimeBand {
  if (uptime === null || uptime === undefined || Number.isNaN(uptime)) return 'nodata';
  if (uptime >= 0.999) return 'ok';
  if (uptime >= 0.99) return 'nominal';
  if (uptime >= 0.95) return 'degraded';
  if (uptime >= 0.7) return 'partial';
  return 'major';
}

/** Band for a day, falling back to the coarse status on pre-uptime snapshots. */
export function dayBand(uptime: number | null | undefined, status: ComponentStatus): UptimeBand {
  if (uptime !== null && uptime !== undefined) return uptimeBand(uptime);
  switch (status) {
    case 'operational':
      return 'ok';
    case 'degraded':
    case 'maintenance':
      return 'degraded';
    case 'partial_outage':
      return 'partial';
    case 'major_outage':
      return 'major';
    default:
      return 'nodata';
  }
}

export function uptimeBandLabel(band: UptimeBand): string {
  return uptimeBandLabels[band];
}

export function uptimeBandClass(band: UptimeBand): string {
  return `band-${band}`;
}

/** Reads the current field, tolerating a snapshot written by the previous collector. */
export function componentUptime(component: StatusComponent): number | null {
  if (component.uptime !== null && component.uptime !== undefined) return component.uptime;
  return component.uptime90d ?? null;
}

/**
 * Approximate minutes of downtime implied by a day's uptime ratio.
 *
 * The uptime check runs once a minute, so the ratio maps directly onto minutes.
 * The current day is only partially elapsed, so it is scaled accordingly.
 */
export function downtimeMinutes(uptime: number, date: string, now: Date = new Date()): number {
  const todayKey = now.toISOString().slice(0, 10);
  const elapsed =
    date === todayKey
      ? Math.max(1, Math.round((now.getTime() - Date.parse(`${date}T00:00:00Z`)) / 60000))
      : MINUTES_PER_DAY;
  return Math.round((1 - uptime) * elapsed);
}

export function statusLabel(status: ComponentStatus): string {
  return statusLabels[status];
}

export function statusHeadline(status: ComponentStatus): string {
  return statusHeadlines[status];
}

export function statusToneClass(status: ComponentStatus): string {
  switch (status) {
    case 'operational':
      return 'tone-ok';
    case 'degraded':
    case 'maintenance':
      return 'tone-warn';
    case 'partial_outage':
      return 'tone-warn-strong';
    case 'major_outage':
      return 'tone-bad';
    case 'unknown':
    default:
      return 'tone-muted';
  }
}

/** Formats an uptime ratio, distinguishing "no data" from a genuine 0%. */
export function formatUptime(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || Number.isNaN(ratio)) return '—';
  return `${(ratio * 100).toFixed(2)}%`;
}

export function formatRelativeTime(date: Date | null): string {
  if (date === null) return '—';
  const diffMs = Date.now() - date.getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 5) return statusMessages.relativeJustNow;
  if (seconds < 60) return statusMessages.relativeSecondsAgo(seconds);
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return statusMessages.relativeMinutesAgo(minutes);
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return statusMessages.relativeHoursAgo(hours);
  const days = Math.floor(hours / 24);
  return statusMessages.relativeDaysAgo(days);
}

export function rollupGroupStatus(snapshot: StatusSnapshot, groupId: string): ComponentStatus {
  const group = snapshot.groups.find((g) => g.id === groupId);
  if (!group) return 'unknown';
  const severities: ComponentStatus[] = [
    'major_outage',
    'partial_outage',
    'degraded',
    'maintenance',
    'unknown',
    'operational',
  ];
  for (const sev of severities) {
    if (group.components.some((c) => c.status === sev)) return sev;
  }
  return 'operational';
}
